import { Component, Input, OnInit, OnDestroy, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatBottomSheet } from '@angular/material/bottom-sheet';

import { SearchStateService } from '../../../shared/services/search-state.service';
import { LocationSearchResultsCardComponent } from '../location-search-results-card/location-search-results-card.component';
import { SortIconComponent } from '../../../../../assets/svg/sort-icon';
import { FilterIconComponent } from '../../../../../assets/svg/filter-icon';
import { TranslateService } from '@ngx-translate/core';
import { DistanceFilterBottomSheetComponent } from '../location-distance-filter-bottom-sheet/distance-filter-bottom-sheet.component';
import { LocationCoordinates } from '../../../../shared/model/LocationCoordinates';
import { MatProgressSpinner } from "@angular/material/progress-spinner";
import { LocationSearchFilters, SearchFilter } from '../../../../shared/model/providerlocationsearch/GetLocationSearchFilterResponse';

@Component({
  selector: 'app-location-search-results-mobile',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatIconModule,
    MatMenuModule,
    LocationSearchResultsCardComponent,
    SortIconComponent,
    FilterIconComponent,
    MatProgressSpinner
],
  templateUrl: './location-search-results.component.html',
  styleUrls: ['./location-search-results.component.css']
})
export class LocationSearchResultsComponent implements OnInit, OnDestroy {
  @Input() isPlus: boolean = false;
  @Input() currentLocation: LocationCoordinates | null = null;
  @Input() cityZipTypeAheadOptions: string[] = []

  searchState = inject(SearchStateService);
  router = inject(Router);
  bottomSheet = inject(MatBottomSheet);

  locationSearchResults$ = this.searchState.locationSearchResults$;
  locationSearchQuery$ = this.searchState.locationSearchQuery$;
  showSpinner$ = this.searchState.showSpinner$;
  locationSortMethod$ = this.searchState.locationSortMethod$;
  locationSearchFilters$ = this.searchState.locationSearchFilters$;
  
  searchType: string = 'Urgent';
  queryCityOrZip: string = '';
  queryNameOrSpecialty: string = '';
  distanceInMiles: number = 10;
  distanceSliderValue: number = 1; // Default to 10 miles (index 1)
  showLocationMenu: boolean = false;
  showSpinner: boolean = false;
  appliedFilterCount = 0;
  isFilterOpen = false;
  allResults: any[] = [];
  locationSortMethod: 'distance' | 'availability'| 'name' = 'distance';
  currentFilters: LocationSearchFilters | null = null;
  
  // Breadcrumb translations
  breadcrumbText: string = 'Location Search';
  breadcrumbText2: string = 'Search Results';
  resultsText: string = 'Results';
  sortByTitle: string = 'Sort by';
  distanceLabel: string = 'Distance';
  availabilityLabel: string = 'Soonest Availabilty';
  nameLabel: string = 'Name (A-Z)';
  mileLabel: string = 'mi';
  locale: string = '';
  
  private destroy$ = new Subject<void>();

  constructor(private translate: TranslateService) {
    this.locale = sessionStorage.getItem('locale') || 'en-US';
  }

  ngOnInit(): void {
    this.translate.use(this.locale);

    // Load translations
    this.translate.get('LOCATION_SEARCH').subscribe((translated: string) => {
      this.breadcrumbText = translated;
    });

    this.translate.get('SEARCH_RESULTS').subscribe((translated: string) => {
      this.breadcrumbText2 = translated;
    });

    this.translate.get('RESULTS').subscribe((translated: string) => {
      this.resultsText = translated;
    });

    this.translate.get('SORT_BY.TITLE').subscribe((translated: string) => {
      this.sortByTitle = translated;
    });

    this.translate.get('SORT_BY.DISTANCE').subscribe((translated: string) => {
      this.distanceLabel = translated;
    });

    this.translate.get('SORT_BY.AVAILABILITY').subscribe((translated: string) => {
      this.availabilityLabel = translated;
    });

    this.translate.get('SORT_BY.NAME').subscribe((translated: string) => {
      this.nameLabel = translated;
    });

      this.translate.get('MILE').subscribe((translated: string) => {
        this.mileLabel = translated;
      });

    // Subscribe to spinner state
    this.showSpinner$.pipe(takeUntil(this.destroy$)).subscribe(show => {
      this.showSpinner = show;
    });

    // Subscribe to search query
    this.locationSearchQuery$
      .pipe(takeUntil(this.destroy$))
      .subscribe(query => {
        if (query) {
          this.searchType = query.facilityOrLocationName || 'Urgent';
          this.queryNameOrSpecialty = query.facilityOrLocationName || '';
          this.queryCityOrZip = query.cityOrZip || '';
        }
      });

    // Subscribe to location search results
    this.locationSearchResults$.pipe(takeUntil(this.destroy$)).subscribe(results => {
      if (results) {
        this.allResults = results;
      }
    });

    // Subscribe to sort method
    this.locationSortMethod$.pipe(takeUntil(this.destroy$)).subscribe(sortMethod => {
      this.locationSortMethod = sortMethod;
    });

    // Subscribe to filter state
    this.searchState.locationSearchFilters$
      // .pipe(takeUntil(this.destroy$))
      .subscribe(filters => {
        this.currentFilters = filters;
        this.appliedFilterCount = this.calculateAppliedFilters(filters);
        
        // Set distance slider value from filters
        if (filters && filters.distanceInMiles) {
          this.setDistanceFilterValue(filters.distanceInMiles);
          this.distanceInMiles = filters.distanceInMiles;
        }
      });

    // Subscribe to filter toggle state
    this.searchState.locationSearchFilterToggle$
      .pipe(takeUntil(this.destroy$))
      .subscribe(isOpen => {
        this.isFilterOpen = isOpen;
      });

    // Subscribe to filter toggle state
    this.searchState.showSpinner$
      .pipe(takeUntil(this.destroy$))
      .subscribe(show => {
        this.showSpinner = show;
      });

    // Close location menu when clicking outside
    document.addEventListener('click', this.closeLocationMenu.bind(this));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    document.removeEventListener('click', this.closeLocationMenu.bind(this));
  }

  private calculateAppliedFilters(filters: any): number {
    let count = 0;
    if (filters) {
      if(filters.serviceList) {
        count += filters.serviceList.filter((option: SearchFilter) => option.isSelected == true).length;
      }
      if(filters.typeList) {
        count += filters.typeList.filter((option: SearchFilter) => option.isSelected == true).length;
      }
    }
    
    // Count the number of active filters
    return count;
  }

  goBack(): void {
    this.router.navigate(['/find-a-provider/mobile']);
  }

  switchToProvider(): void {
    // Navigate to provider search results or switch view
    this.router.navigate(['/find-a-provider/mobile/provider-search-results']);
  }

  toggleLocationMenu(): void {
    this.onOpenDistanceFilterBottomSheet();
  }

  closeLocationMenu(): void {
    this.showLocationMenu = false;
  }

  clearLocationFilter(): void {
    this.queryCityOrZip = '';
    this.distanceSliderValue = 1;
    this.distanceInMiles = 10;
  }

  applyLocationFilter(): void {
    // Map slider value to distance
    const distanceMap = [5, 10, 15, 25, 50];
    this.distanceInMiles = distanceMap[this.distanceSliderValue];
    
    // Update search query with new location and distance
    this.searchState.setLocationSearchQuery({
      facilityOrLocationName: this.searchType,
      cityOrZip: this.queryCityOrZip
    });
    
    // Close the menu
    this.showLocationMenu = false;
  }

  setDistanceFilterValue(distanceInMiles: number): void {
    // Map distance in miles to slider value
    switch (distanceInMiles) {
      case 5:
        this.distanceSliderValue = 0;
        break;
      case 10:
        this.distanceSliderValue = 1;
        break;
      case 15:
        this.distanceSliderValue = 2;
        break;
      case 25:
        this.distanceSliderValue = 3;
        break;
      case 50:
        this.distanceSliderValue = 4;
        break;
      default:
        this.distanceSliderValue = 2; // Default to 15 miles
    }
  }

  onOpenDistanceFilterBottomSheet(): void {
    const bottomSheetRef = this.bottomSheet.open(DistanceFilterBottomSheetComponent, {
      data: {
        queryCityOrZip: this.queryCityOrZip,
        distanceSliderValue: this.distanceSliderValue,
        currentFilters: this.currentFilters,
        queryNameOrSpecialty: this.queryNameOrSpecialty,
        currentLocation: this.currentLocation,
        locationSortMethod: this.locationSortMethod,
        allResults: this.allResults,
        cityZipTypeAheadOptions: this.cityZipTypeAheadOptions
      }
    });

    bottomSheetRef.afterDismissed().subscribe((result) => {
      if (result) {
        // Update local state from bottom sheet result
        this.queryCityOrZip = result.queryCityOrZip;
        this.distanceSliderValue = result.distanceSliderValue;
      }
    });
  }

  toggleFilter(): void {
    this.searchState.setLocationSearchFilterToggle(!this.isFilterOpen);
  }

  clearSearch(): void {

    this.searchState.setLocationSearchQuery({
      facilityOrLocationName: '',
      cityOrZip: this.queryCityOrZip
    });
    this.searchState.clearLocationSearchResults();
    this.clearFilters();
    this.searchState.setProviderSearchFilterToggle(false);
    this.searchState.clearCurrentLocationDetails();

  }

  clearFilters(): void {
    if (this.currentFilters) {
      // Clear all selections
      this.currentFilters.serviceList?.forEach(option => option.isSelected = false);
      this.currentFilters.typeList?.forEach(option => option.isSelected = false);
    
      this.searchState.setLocationSearchFilters(this.currentFilters);
    }
  }

  onApplySortMethod(sortBy: 'distance' | 'availability' | 'name'): void {
    this.searchState.setLocationSortMethod(sortBy);
    this.searchState.setLocationSearchResults(this.searchState.sortLocationSearchResults(this.allResults, sortBy));
  }
}
